/**
 * Transition Rules: EnergyPlus 9.1 to 9.2
 *
 * Includes complex TABLE:* object transformations that create multiple new objects.
 */

import type { VersionTransition, ObjectTransitionRule, MultiObjectResult } from '../types';
import type { IDFObject, IDFDocument, IDFField } from '../../parser/types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';
import { createObject, addChange } from '../engine';

/**
 * Transform TABLE:ONEINDEPENDENTVARIABLE to new Table:* format
 * Creates: Table:IndependentVariable, Table:IndependentVariableList, Table:Lookup
 */
function transformTableOneIndependentVariable(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const name = object.fields[0]?.value || 'Table';
  const minX = object.fields[3]?.value || '';
  const maxX = object.fields[4]?.value || '';
  const minOutput = object.fields[5]?.value || '';
  const maxOutput = object.fields[6]?.value || '';
  const inputUnitType = object.fields[7]?.value || 'Dimensionless';
  const outputUnitType = object.fields[8]?.value || 'Dimensionless';

  // Extract X values and output values from pairs starting at field 9
  const xValues: string[] = [];
  const outputValues: string[] = [];
  for (let i = 9; i < object.fields.length; i += 2) {
    const xField = object.fields[i];
    const outputField = object.fields[i + 1];
    if (xField) {
      xValues.push(xField.value);
    }
    if (outputField) {
      outputValues.push(outputField.value);
    }
  }

  const indepVarName = `${name}_IndependentVariable1`;
  const indepVarListName = `${name}_IndependentVariableList`;

  // Determine interpolation method for new format
  const interpMethodRaw = object.fields[2]?.value || 'Linear';
  const newInterpMethod = interpMethodRaw.toUpperCase() === 'LAGRANGEINTERPOLATIONLINEAREXTRAPOLATION' ? 'Linear' :
             interpMethodRaw.toUpperCase().includes('CUBIC') ? 'Cubic' : 'Linear';

  // Create Table:IndependentVariable
  const indepVarFields: IDFField[] = [
    { value: indepVarName },
    { value: newInterpMethod },
    { value: 'Constant' }, // Extrapolation Method
    { value: minX },
    { value: maxX },
    { value: '' }, // Normalization Reference Value
    { value: inputUnitType },
    { value: '' }, // External File Name
    { value: '' }, // External File Column Number
    { value: '' }, // External File Starting Row Number
    ...xValues.map(v => ({ value: v })),
  ];

  const indepVarObject = createObject(
    `${object.id}_indepvar`,
    'Table:IndependentVariable',
    indepVarFields,
    [],
    object.sourceLocation
  );

  // Create Table:IndependentVariableList
  const indepVarListObject = createObject(
    `${object.id}_indepvarlist`,
    'Table:IndependentVariableList',
    [
      { value: indepVarListName },
      { value: indepVarName },
    ],
    [],
    object.sourceLocation
  );

  // Create Table:Lookup (primary object)
  const lookupFields: IDFField[] = [
    { value: name }, // Keep original name
    { value: indepVarListName },
    { value: 'None' }, // Normalization Method
    { value: '' }, // Normalization Divisor
    { value: minOutput },
    { value: maxOutput },
    { value: outputUnitType },
    { value: '' }, // External File Name
    { value: '' }, // External File Column Number
    { value: '' }, // External File Starting Row Number
    ...outputValues.map(v => ({ value: v })),
  ];

  const lookupObject = createObject(
    object.id,
    'Table:Lookup',
    lookupFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  result.primary = lookupObject;
  result.additional.push(indepVarObject, indepVarListObject);
  addChange(
    result.changes,
    'object_renamed',
    object.className,
    `Converted ${object.className} "${name}" to Table:Lookup with supporting Table:IndependentVariable and Table:IndependentVariableList objects`,
    name
  );

  return result;
}

/**
 * Transform TABLE:TWOINDEPENDENTVARIABLES to new Table:* format
 */
function transformTableTwoIndependentVariables(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const name = object.fields[0]?.value || 'Table';
  const minX1 = object.fields[3]?.value || '';
  const maxX1 = object.fields[4]?.value || '';
  const minX2 = object.fields[5]?.value || '';
  const maxX2 = object.fields[6]?.value || '';
  const minOutput = object.fields[7]?.value || '';
  const maxOutput = object.fields[8]?.value || '';
  const inputUnitType1 = object.fields[9]?.value || 'Dimensionless';
  const inputUnitType2 = object.fields[10]?.value || 'Dimensionless';
  const outputUnitType = object.fields[11]?.value || 'Dimensionless';

  // Extract X1, X2, and output values - format depends on normalization point
  // Simplified: assume values start at field 12 as X1, X2, output triplets
  const x1Values: string[] = [];
  const x2Values: string[] = [];
  const outputValues: string[] = [];

  // Parse the triplets
  for (let i = 12; i < object.fields.length; i += 3) {
    const x1Field = object.fields[i];
    const x2Field = object.fields[i + 1];
    const outField = object.fields[i + 2];
    if (x1Field) x1Values.push(x1Field.value);
    if (x2Field) x2Values.push(x2Field.value);
    if (outField) outputValues.push(outField.value);
  }

  // Deduplicate x1 and x2 values
  const uniqueX1 = [...new Set(x1Values)];
  const uniqueX2 = [...new Set(x2Values)];

  const indepVar1Name = `${name}_IndependentVariable1`;
  const indepVar2Name = `${name}_IndependentVariable2`;
  const indepVarListName = `${name}_IndependentVariableList`;

  // Create Table:IndependentVariable for X1
  const indepVar1Object = createObject(
    `${object.id}_indepvar1`,
    'Table:IndependentVariable',
    [
      { value: indepVar1Name },
      { value: 'Linear' },
      { value: 'Constant' },
      { value: minX1 },
      { value: maxX1 },
      { value: '' },
      { value: inputUnitType1 },
      { value: '' },
      { value: '' },
      { value: '' },
      ...uniqueX1.map(v => ({ value: v })),
    ],
    [],
    object.sourceLocation
  );

  // Create Table:IndependentVariable for X2
  const indepVar2Object = createObject(
    `${object.id}_indepvar2`,
    'Table:IndependentVariable',
    [
      { value: indepVar2Name },
      { value: 'Linear' },
      { value: 'Constant' },
      { value: minX2 },
      { value: maxX2 },
      { value: '' },
      { value: inputUnitType2 },
      { value: '' },
      { value: '' },
      { value: '' },
      ...uniqueX2.map(v => ({ value: v })),
    ],
    [],
    object.sourceLocation
  );

  // Create Table:IndependentVariableList
  const indepVarListObject = createObject(
    `${object.id}_indepvarlist`,
    'Table:IndependentVariableList',
    [
      { value: indepVarListName },
      { value: indepVar1Name },
      { value: indepVar2Name },
    ],
    [],
    object.sourceLocation
  );

  // Create Table:Lookup
  const lookupObject = createObject(
    object.id,
    'Table:Lookup',
    [
      { value: name },
      { value: indepVarListName },
      { value: 'None' },
      { value: '' },
      { value: minOutput },
      { value: maxOutput },
      { value: outputUnitType },
      { value: '' },
      { value: '' },
      { value: '' },
      ...outputValues.map(v => ({ value: v })),
    ],
    [...object.leadingComments],
    object.sourceLocation
  );

  result.primary = lookupObject;
  result.additional.push(indepVar1Object, indepVar2Object, indepVarListObject);
  addChange(
    result.changes,
    'object_renamed',
    object.className,
    `Converted ${object.className} "${name}" to Table:Lookup with 2 Table:IndependentVariable and Table:IndependentVariableList objects`,
    name
  );

  return result;
}

/**
 * Transform TABLE:MULTIVARIABLELOOKUP to new Table:* format
 */
function transformTableMultiVariableLookup(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const name = object.fields[0]?.value || 'Table';
  const numIndepVars = parseInt(object.fields[2]?.value || '1', 10);
  const minOutput = object.fields[5]?.value || '';
  const maxOutput = object.fields[6]?.value || '';
  const outputUnitType = object.fields[7]?.value || 'Dimensionless';

  // Parse independent variable information
  // Starting at field 8: for each independent variable:
  // Unit Type, Interpolation Method, Min, Max
  const indepVarObjects: IDFObject[] = [];
  const indepVarNames: string[] = [];

  let fieldIndex = 8;
  for (let v = 0; v < numIndepVars; v++) {
    const unitType = object.fields[fieldIndex]?.value || 'Dimensionless';
    const interpMethod = object.fields[fieldIndex + 1]?.value || 'Linear';
    const minVal = object.fields[fieldIndex + 2]?.value || '';
    const maxVal = object.fields[fieldIndex + 3]?.value || '';
    fieldIndex += 4;

    const varName = `${name}_IndependentVariable${String(v + 1)}`;
    indepVarNames.push(varName);

    indepVarObjects.push(createObject(
      `${object.id}_indepvar${String(v + 1)}`,
      'Table:IndependentVariable',
      [
        { value: varName },
        { value: interpMethod.toUpperCase().includes('CUBIC') ? 'Cubic' : 'Linear' },
        { value: 'Constant' },
        { value: minVal },
        { value: maxVal },
        { value: '' },
        { value: unitType },
        { value: '' },
        { value: '' },
        { value: '' },
        // Values will need to be extracted from the data section
      ],
      [],
      object.sourceLocation
    ));
  }

  const indepVarListName = `${name}_IndependentVariableList`;

  // Create Table:IndependentVariableList
  const indepVarListObject = createObject(
    `${object.id}_indepvarlist`,
    'Table:IndependentVariableList',
    [
      { value: indepVarListName },
      ...indepVarNames.map(n => ({ value: n })),
    ],
    [],
    object.sourceLocation
  );

  // Extract output values from remaining fields
  const outputValues: string[] = [];
  for (let i = fieldIndex; i < object.fields.length; i++) {
    const field = object.fields[i];
    if (field) {
      outputValues.push(field.value);
    }
  }

  // Create Table:Lookup
  const lookupObject = createObject(
    object.id,
    'Table:Lookup',
    [
      { value: name },
      { value: indepVarListName },
      { value: 'None' },
      { value: '' },
      { value: minOutput },
      { value: maxOutput },
      { value: outputUnitType },
      { value: '' },
      { value: '' },
      { value: '' },
      ...outputValues.map(v => ({ value: v })),
    ],
    [...object.leadingComments],
    object.sourceLocation
  );

  result.primary = lookupObject;
  result.additional.push(...indepVarObjects, indepVarListObject);
  addChange(
    result.changes,
    'object_renamed',
    object.className,
    `Converted ${object.className} "${name}" to Table:Lookup with ${String(numIndepVars)} Table:IndependentVariable and Table:IndependentVariableList objects`,
    name
  );

  const warning = {
    message: `TABLE:MULTIVARIABLELOOKUP conversion may need manual review - independent variable values were not fully extracted`,
    objectClass: object.className,
  };
  if (name) {
    (warning as { objectName?: string }).objectName = name;
  }
  result.warnings.push(warning);

  return result;
}

export function createTransition_9_1_to_9_2(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Foundation:Kiva - Insert blank field at position 2
  objectRules.set('FOUNDATION:KIVA', {
    sourceClass: 'Foundation:Kiva',
    targetClass: 'Foundation:Kiva',
    fieldMappings: [
      { sourceIndex: 0, targetIndex: 0, type: 'copy' },
      { sourceIndex: -1, targetIndex: 1, type: 'add_default', defaultValue: '' },
      ...createShiftedCopyMappings(1, 2, 50),
    ],
  });

  // Schedule:File - Change "FIXED" to "SPACE" in field 7
  objectRules.set('SCHEDULE:FILE', {
    sourceClass: 'Schedule:File',
    targetClass: 'Schedule:File',
    fieldMappings: [
      ...createCopyMappings(0, 6), // Fields 0-5
      {
        sourceIndex: 6,
        targetIndex: 6,
        type: 'transform',
        transform: (value) => value.toUpperCase() === 'FIXED' ? 'Space' : value,
      },
      ...createShiftedCopyMappings(7, 7, 10),
    ],
  });

  // ThermalStorage:Ice:Detailed - Transform curve type names
  objectRules.set('THERMALSTORAGE:ICE:DETAILED', {
    sourceClass: 'ThermalStorage:Ice:Detailed',
    targetClass: 'ThermalStorage:Ice:Detailed',
    fieldMappings: [
      ...createCopyMappings(0, 5), // Fields 0-4
      {
        sourceIndex: 5,
        targetIndex: 5,
        type: 'transform',
        transform: (value) => value.toUpperCase() === 'QUADRATICLINEAR' ? 'FractionDischargedLMTD' : value,
      },
      { sourceIndex: 6, targetIndex: 6, type: 'copy' },
      {
        sourceIndex: 7,
        targetIndex: 7,
        type: 'transform',
        transform: (value) => value.toUpperCase() === 'CUBICLINEAR' ? 'LMTDMassFlow' : value,
      },
      ...createShiftedCopyMappings(8, 8, 20),
    ],
  });

  // TABLE:ONEINDEPENDENTVARIABLE → Table:IndependentVariable + Table:IndependentVariableList + Table:Lookup
  objectRules.set('TABLE:ONEINDEPENDENTVARIABLE', {
    sourceClass: 'Table:OneIndependentVariable',
    targetClass: 'Table:Lookup',
    fieldMappings: [],
    multiObjectTransform: transformTableOneIndependentVariable,
  });

  // TABLE:TWOINDEPENDENTVARIABLES → 2x Table:IndependentVariable + Table:IndependentVariableList + Table:Lookup
  objectRules.set('TABLE:TWOINDEPENDENTVARIABLES', {
    sourceClass: 'Table:TwoIndependentVariables',
    targetClass: 'Table:Lookup',
    fieldMappings: [],
    multiObjectTransform: transformTableTwoIndependentVariables,
  });

  // TABLE:MULTIVARIABLELOOKUP → Nx Table:IndependentVariable + Table:IndependentVariableList + Table:Lookup
  objectRules.set('TABLE:MULTIVARIABLELOOKUP', {
    sourceClass: 'Table:MultiVariableLookup',
    targetClass: 'Table:Lookup',
    fieldMappings: [],
    multiObjectTransform: transformTableMultiVariableLookup,
  });

  return {
    fromVersion: { major: 9, minor: 1 },
    toVersion: { major: 9, minor: 2 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
